import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Paper,
  Tooltip,
  Menu,
  MenuItem,
  Fade
} from '@mui/material';
import {
  ArrowBackIos as ArrowLeftIcon,
  ArrowForwardIos as ArrowRightIcon,
  CalendarToday as CalendarIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Today as TodayIcon,
  History as HistoryIcon
} from '@mui/icons-material';

const YearNavigation = ({ individualId, currentYear, onYearChange }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  
  const currentActualYear = new Date().getFullYear();

  useEffect(() => {
    if (individualId) {
      fetchAvailableYears();
    }
  }, [individualId]);

  const fetchAvailableYears = async () => {
    try {
      setLoading(true);
      console.log('YearNavigation: Fetching years for individual', individualId);
      
      // First try to get years where the individual has actual form data
      const dataResponse = await fetchWithAuth(`/api/individuals/${individualId}/years-with-data`);
      
      console.log('YearNavigation: Data response status', dataResponse.status);
      
      if (dataResponse.ok) {
        const yearsWithData = await dataResponse.json();
        console.log('YearNavigation: Years with data:', yearsWithData);
        
        if (yearsWithData.length > 0) {
          // Use years with actual data
          const sortedYears = yearsWithData.sort((a, b) => b - a);
          setAvailableYears(sortedYears);
          
          // If no current year is set, use the most recent year with data
          if (!currentYear) {
            console.log('YearNavigation: Setting year to', sortedYears[0]);
            onYearChange(sortedYears[0]);
          }
        } else {
          console.log('YearNavigation: No years with data, trying cycles');
          // Fallback to cycles if no form data exists
          const cyclesResponse = await fetchWithAuth(`/api/individuals/${individualId}/cycles`);
          
          if (cyclesResponse.ok) {
            const cycles = await cyclesResponse.json();
            const years = cycles.map(cycle => cycle.cycle_year).sort((a, b) => b - a);
            setAvailableYears(years);
            
            // If no current year is set, use the most recent cycle year
            if (!currentYear && years.length > 0) {
              console.log('YearNavigation: Setting year to cycle year', years[0]);
              onYearChange(years[0]);
            } else if (!currentYear && years.length === 0) {
              // If no cycles exist, default to current year
              const defaultYear = new Date().getFullYear();
              console.log('YearNavigation: No cycles found, defaulting to current year', defaultYear);
              setAvailableYears([defaultYear]);
              onYearChange(defaultYear);
            }
          } else {
            // If cycles API fails, default to current year
            const defaultYear = new Date().getFullYear();
            console.log('YearNavigation: Cycles API failed, defaulting to current year', defaultYear);
            setAvailableYears([defaultYear]);
            onYearChange(defaultYear);
          }
        }
      } else {
        const errorText = await dataResponse.text();
        console.error('YearNavigation: API error:', errorText);
        setError('Failed to fetch years with data');
      }
    } catch (err) {
      console.error('Error fetching available years:', err);
      setError('Error loading years');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousYear = () => {
    const currentIndex = availableYears.indexOf(currentYear);
    if (currentIndex < availableYears.length - 1) {
      onYearChange(availableYears[currentIndex + 1]);
    }
  };

  const handleNextYear = () => {
    const currentIndex = availableYears.indexOf(currentYear);
    if (currentIndex > 0) {
      onYearChange(availableYears[currentIndex - 1]);
    }
  };

  const canGoPrevious = () => {
    const currentIndex = availableYears.indexOf(currentYear);
    return currentIndex < availableYears.length - 1;
  };

  const canGoNext = () => {
    const currentIndex = availableYears.indexOf(currentYear);
    return currentIndex > 0;
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleYearSelect = (year) => {
    onYearChange(year);
    handleMenuClose();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading years...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    );
  }

  if (availableYears.length === 0 || !currentYear) {
    return (
      <Typography variant="body2" color="text.secondary">
        No form cycles available
      </Typography>
    );
  }

  // Ensure currentYear is valid
  const displayYear = currentYear || new Date().getFullYear();
  const isCurrentYear = displayYear === currentActualYear;

  return (
    <Paper 
      elevation={3}
      sx={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        backgroundColor: 'background.paper',
        borderRadius: 3,
        overflow: 'hidden',
        border: '2px solid',
        borderColor: isCurrentYear ? 'primary.main' : 'divider',
        position: 'relative'
      }}
    >
      {/* Left Arrow */}
      <Tooltip title={canGoPrevious() ? `Go to ${availableYears[availableYears.indexOf(currentYear) + 1]}` : 'No earlier years'}>
        <span>
          <IconButton 
            onClick={handlePreviousYear}
            disabled={!canGoPrevious()}
            size="large"
            sx={{ 
              borderRadius: 0,
              backgroundColor: canGoPrevious() ? 'primary.light' : 'action.disabledBackground',
              color: canGoPrevious() ? 'primary.main' : 'text.disabled',
              '&:hover': {
                backgroundColor: canGoPrevious() ? 'primary.main' : 'action.disabledBackground',
                color: canGoPrevious() ? 'white' : 'text.disabled'
              },
              transition: 'all 0.2s ease',
              px: 2
            }}
          >
            <ArrowLeftIcon />
          </IconButton>
        </span>
      </Tooltip>
      
      {/* Center Year Display */}
      <Box 
        sx={{ 
          px: 4, 
          py: 2,
          backgroundColor: isCurrentYear ? 'primary.main' : 'background.default',
          color: isCurrentYear ? 'white' : 'text.primary',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: '200px',
          cursor: availableYears.length > 1 ? 'pointer' : 'default',
          '&:hover': availableYears.length > 1 ? {
            backgroundColor: isCurrentYear ? 'primary.dark' : 'action.hover',
          } : {},
          transition: 'background-color 0.2s ease',
          userSelect: 'none',
          position: 'relative'
        }}
        onClick={availableYears.length > 1 ? handleMenuClick : undefined}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isCurrentYear ? (
            <TodayIcon sx={{ fontSize: '1.2rem' }} />
          ) : (
            <HistoryIcon sx={{ fontSize: '1.2rem', opacity: 0.7 }} />
          )}
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 'bold',
              letterSpacing: '0.5px'
            }}
          >
            {displayYear}
          </Typography>
          {availableYears.length > 1 && (
            <ArrowDownIcon sx={{ fontSize: '1.2rem', ml: 0.5 }} />
          )}
        </Box>
        <Typography 
          variant="caption" 
          sx={{ 
            textTransform: 'uppercase',
            letterSpacing: '1px',
            opacity: 0.9,
            fontWeight: 'medium'
          }}
        >
          {isCurrentYear ? 'Current Cycle' : 'Form Cycle'}
        </Typography>
      </Box>

      {/* Year Selection Menu */}
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: '200px',
            maxHeight: '300px'
          }
        }}
      >
        {availableYears.map((year) => (
          <MenuItem 
            key={year} 
            onClick={() => handleYearSelect(year)}
            selected={year === currentYear}
            sx={{
              py: 1.5,
              px: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white'
                }
              }
            }}
          >
            <Typography>{year}</Typography>
            {year === currentActualYear && (
              <Chip 
                label="Current" 
                size="small" 
                color="primary"
                sx={{ ml: 2, height: 20 }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>
      
      {/* Right Arrow */}
      <Tooltip title={canGoNext() ? `Go to ${availableYears[availableYears.indexOf(currentYear) - 1]}` : 'No later years'}>
        <span>
          <IconButton 
            onClick={handleNextYear}
            disabled={!canGoNext()}
            size="large"
            sx={{ 
              borderRadius: 0,
              backgroundColor: canGoNext() ? 'primary.light' : 'action.disabledBackground',
              color: canGoNext() ? 'primary.main' : 'text.disabled',
              '&:hover': {
                backgroundColor: canGoNext() ? 'primary.main' : 'action.disabledBackground',
                color: canGoNext() ? 'white' : 'text.disabled'
              },
              transition: 'all 0.2s ease',
              px: 2
            }}
          >
            <ArrowRightIcon />
          </IconButton>
        </span>
      </Tooltip>

      {/* Progress Indicator */}
      {availableYears.length > 1 && (
        <Box 
          sx={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 4,
            backgroundColor: 'action.hover'
          }}
        >
          <Box 
            sx={{ 
              height: '100%',
              backgroundColor: 'primary.main',
              width: `${((availableYears.length - availableYears.indexOf(currentYear)) / availableYears.length) * 100}%`,
              transition: 'width 0.3s ease'
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default YearNavigation;
